import { Link } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import { Tooltip } from 'react-tooltip';

import messages from './messages';
import { CustomButton } from '../button';
import { PlusIcon, WasteIcon } from '../svgIcons';

export const ViewAllLink = ({ link }: Object) => (
  <Link to={link} className="dib mt2 fr brand link">
    <span>
      <FormattedMessage {...messages.viewAll} />
    </span>
  </Link>
);

export const AddButton = () => (
  <CustomButton className="brand bg-transparent ba b--brand barlow-condensed pv1">
    <PlusIcon className="v-mid h1 w1" />
    <span className="v-mid f4 fw6 ttu pl2">
      <FormattedMessage {...messages.new} />
    </span>
  </CustomButton>
);

/**
 * A button for deleting something
 * @param {string} className Additional css classes
 * @param {MouseEventHandler<T>} onClick The action to call on click
 * @param {boolean} [showText=true] true if the message should be shown
 * @typedef {import('@formatjs/intl').MessageDescriptor} MessageDescriptor
 * @param {MessageDescriptor} message The message to show
 * @returns {Element} The delete button
 * @constructor
 */
export const DeleteButton = ({
  className,
  onClick,
  showText = true,
  message = messages.delete,
}: Object) => {
  const intl = useIntl();
  return (
    <CustomButton className={`red bg-transparent ba b--red pv1 ${className}`} onClick={onClick}>
      <div
        data-tooltip-id="Delete"
        data-tooltip-content={!showText ? intl.formatMessage(message) : ''}
      >
        <WasteIcon className="v-mid h1 w1" />
        {showText && (
          <span className="v-mid f4 fw6 ttu barlow-condensed pl2">
            <FormattedMessage {...message} />
          </span>
        )}
      </div>
      <Tooltip effect="solid" id="Delete" />
    </CustomButton>
  );
};

export function VisibilityBox({ visibility, extraClasses }: Object) {
  let color = visibility === 'PUBLIC' ? 'blue-grey' : 'brand';
  let borderColor = visibility === 'PUBLIC' ? 'b--grey' : 'b--brand';
  const text = visibility ? <FormattedMessage {...messages[visibility.toLowerCase()]} /> : '';

  return <div className={`tc br1 f7 ttu ba ${borderColor} ${color} ${extraClasses}`}>{text}</div>;
}

export function JoinMethodBox(props) {
  const teamJoinMethods = {
    ANY: 'anyoneCanJoin',
    BY_REQUEST: 'byRequest',
    BY_INVITE: 'byInvite',
  };
  return (
    <div className={`tc br1 f7 ttu ba brand b--brand ${props.className}`}>
      <FormattedMessage {...messages[teamJoinMethods[props.joinMethod]]} />
    </div>
  );
}

export function Management(props) {
  // admin users can switch between all teams/orgs and only their teams/orgs
  return (
    <div className="pull-center cf">
      <div className="cf pt4 mb2">
        <h3 className="barlow-condensed f2 ma0 dib v-mid ttu">{props.title}</h3>
        {props.showAddButton && (
          <Link to={'new/'} className="dib ml3">
            <AddButton />
          </Link>
        )}
        {props.isAdmin && (
          <div className="mt2 mb3">
            <CustomButton
              className={`link di f6 mr2 ph3 pv2 ba b--grey-light ${
                props.userOnly ? 'bg-white blue-grey' : 'bg-blue-grey white fw5'
              }`}
              onClick={() => {
                props.setUserOnly(false);
              }}
            >
              <FormattedMessage {...messages.all} />
            </CustomButton>
            <CustomButton
              className={`link di f6 mh1 ph3 pv2 ba b--grey-light ${
                props.userOnly ? 'bg-blue-grey white fw5' : 'bg-white blue-grey'
              }`}
              onClick={() => {
                props.setUserOnly(true);
              }}
            >
              {props.userOnlyLabel}
            </CustomButton>
          </div>
        )}
      </div>

      <div className="w-100 cf dib">{props.children}</div>
    </div>
  );
}
